import { PointerEvent as ReactPointerEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

// A Tinder-style deck used for both films and actors. Each of the four directions is an answer; the
// directions are labelled on the card's edges (lit up while dragging) and repeated as buttons and keys.
export type Direction = 'right' | 'left' | 'up' | 'down';
export type DeckAction = { direction: Direction; key: string; label: string; stamp: string; icon: string; tone: 'green' | 'red' | 'violet' | 'gold' | 'grey'; save: (item: any) => Promise<unknown>; undo: (item: any) => Promise<unknown> };
type Props = {
  load: () => Promise<{ items: any[]; stats?: any }>;
  actions: DeckAction[];
  renderCard: (item: any) => ReactNode;
  backdrop: (item: any) => string | undefined;
  onAnswered?: (item: any, action: DeckAction) => void;
  onStats?: (stats: any) => void;
  emptyText: string;
  // Extra controls under the buttons for the current card; `answer` lets them answer it (optionally with their own save).
  extra?: (item: any, answer: (direction: Direction, override?: Partial<DeckAction>) => void) => ReactNode;
};

const THRESHOLD = 110;
const order: Direction[] = ['left', 'up', 'down', 'right'];
const arrows: Record<Direction, string> = { left: '←', right: '→', up: '↑', down: '↓' };
const fly: Record<Direction, string> = { right: 'translate(140%, 0) rotate(24deg)', left: 'translate(-140%, 0) rotate(-24deg)', up: 'translate(0, -140%)', down: 'translate(0, 140%) rotate(6deg)' };
const keyDirection: Record<string, Direction> = { ArrowRight: 'right', ArrowLeft: 'left', ArrowUp: 'up', ArrowDown: 'down' };

const SwipeDeck = ({ load, actions, renderCard, backdrop, onAnswered, onStats, emptyText, extra }: Props) => {
  const [deck, setDeck] = useState<any[]>([]);
  const [answered, setAnswered] = useState<{ item: any; action: DeckAction }[]>([]);
  const [drag, setDrag] = useState({ x: 0, y: 0, active: false });
  const [leaving, setLeaving] = useState<Direction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const start = useRef<{ x: number; y: number } | null>(null);
  const fetching = useRef(false);
  const byDirection = Object.fromEntries(actions.map(action => [action.direction, action])) as Record<Direction, DeckAction>;

  const loadMore = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    try { const data = await load(); if (data.stats) onStats?.(data.stats); setDeck(current => { const ids = new Set(current.map(item => item.id)); return [...current, ...data.items.filter(item => !ids.has(item.id))]; }); }
    catch { setError('We couldn’t load the deck. Please try again.'); } finally { fetching.current = false; setLoading(false); }
  }, [load, onStats]);
  useEffect(() => { loadMore(); }, [loadMore]);
  useEffect(() => { if (deck.length > 0 && deck.length < 5) loadMore(); }, [deck.length, loadMore]);

  // Swiping must never become the browser's back gesture or pull-to-refresh.
  useEffect(() => {
    const root = document.documentElement, body = document.body;
    const previous = [root.style.overscrollBehavior, body.style.overscrollBehavior];
    root.style.overscrollBehavior = 'none'; body.style.overscrollBehavior = 'none';
    return () => { root.style.overscrollBehavior = previous[0]; body.style.overscrollBehavior = previous[1]; };
  }, []);

  const current = deck[0];
  const answer = useCallback((direction: Direction, override?: Partial<DeckAction>) => {
    const action = byDirection[direction] && { ...byDirection[direction], ...override };
    if (!current || leaving || !action) return;
    setLeaving(direction);
    window.setTimeout(() => {
      setDeck(rest => rest.slice(1)); setLeaving(null); setDrag({ x: 0, y: 0, active: false });
      setAnswered(list => [{ item: current, action }, ...list].slice(0, 20));
      onAnswered?.(current, action);
    }, 260);
    action.save(current).catch(() => setError('That answer didn’t save. Undo and try again.'));
  }, [byDirection, current, leaving, onAnswered]);

  const undo = useCallback(() => {
    const [last, ...rest] = answered;
    if (!last || leaving) return;
    setAnswered(rest); setDeck(list => [last.item, ...list]); setError('');
    last.action.undo(last.item).catch(() => undefined);
    onAnswered?.(last.item, { ...last.action, key: `undo:${last.action.key}` });
  }, [answered, leaving, onAnswered]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement)?.tagName === 'INPUT') return;
      if (keyDirection[event.key]) { event.preventDefault(); answer(keyDirection[event.key]); }
      else if (event.key === 'Backspace' || (event.key === 'z' && (event.metaKey || event.ctrlKey))) { event.preventDefault(); undo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [answer, undo]);

  const onDown = (event: ReactPointerEvent) => { start.current = { x: event.clientX, y: event.clientY }; (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId); setDrag(d => ({ ...d, active: true })); };
  const onMove = (event: ReactPointerEvent) => { if (start.current) setDrag({ x: event.clientX - start.current.x, y: event.clientY - start.current.y, active: true }); };
  const onUp = () => {
    if (!start.current) return;
    start.current = null;
    const vertical = Math.abs(drag.y) > Math.abs(drag.x);
    if (vertical && drag.y < -THRESHOLD) answer('up');
    else if (vertical && drag.y > THRESHOLD) answer('down');
    else if (!vertical && drag.x > THRESHOLD) answer('right');
    else if (!vertical && drag.x < -THRESHOLD) answer('left');
    else setDrag({ x: 0, y: 0, active: false });
  };

  const vertical = Math.abs(drag.y) > Math.abs(drag.x);
  const hint: Direction | null = leaving || (vertical ? (drag.y < -40 ? 'up' : drag.y > 40 ? 'down' : null) : drag.x > 40 ? 'right' : drag.x < -40 ? 'left' : null);
  const transform = leaving ? fly[leaving] : `translate(${drag.x}px, ${drag.y}px) rotate(${drag.x / 18}deg)`;

  return <div className="deck">
    <div className="swipe__stage">
      {deck.slice(1, 3).reverse().map((item, index, list) => <article key={item.id} className="swipe-card swipe-card--behind" style={{ transform: `scale(${0.94 + (index / Math.max(1, list.length)) * 0.03}) translateY(${(list.length - index) * 14}px)` }} aria-hidden="true"><img src={backdrop(item)} alt="" /></article>)}
      {current ? <article key={current.id} className={`swipe-card${drag.active && !leaving ? ' is-dragging' : ''}${leaving ? ' is-leaving' : ''}`} style={{ transform }} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} aria-label="Swipe, use the arrow keys, or use the buttons below to answer">
        <img src={backdrop(current)} alt="" draggable={false} />
        {/* The four answers, labelled on the edges they swipe towards. */}
        {actions.map(action => <span key={action.direction} className={`deck-edge deck-edge--${action.direction} tone-${action.tone}${hint === action.direction ? ' is-active' : ''}${hint && hint !== action.direction ? ' is-dim' : ''}`} aria-hidden="true">{action.direction === 'left' || action.direction === 'up' ? `${arrows[action.direction]} ` : ''}{action.label}{action.direction === 'right' || action.direction === 'down' ? ` ${arrows[action.direction]}` : ''}</span>)}
        {hint && <span className={`swipe-stamp tone-${byDirection[hint].tone}`}>{byDirection[hint].stamp}</span>}
        {renderCard(current)}
      </article> : <div className="swipe__empty-card">{loading ? 'Shuffling the deck…' : error || emptyText}</div>}
    </div>

    <div className="deck-buttons" role="group" aria-label="Answer">
      <button className="deck-btn deck-btn--undo" onClick={undo} disabled={!answered.length} title="Undo (Backspace)"><span aria-hidden="true">↶</span><small>Undo</small></button>
      {order.map(direction => byDirection[direction]).filter(Boolean).map(action => <button key={action.direction} className={`deck-btn tone-${action.tone}`} onClick={() => answer(action.direction)} disabled={!current} title={`${action.label} (${arrows[action.direction]})`}>
        <span aria-hidden="true">{action.icon}</span><small>{action.label}</small><em aria-hidden="true">{arrows[action.direction]}</em>
      </button>)}
    </div>
    {current && extra?.(current, answer)}
    {error && current && <p className="swipe__error">{error}</p>}
  </div>;
};

export default SwipeDeck;
